/**
 * Il drawer di dettaglio della riga del tabellone: la domanda di Aida, i fatti,
 * i riferimenti, i campi compilabili con la loro provenienza, la partenza.
 *
 * Vive qui e non dentro la tab perché dal #39 le viste sulla stessa riga sono
 * due — il tabellone e il calendario. Duplicarlo avrebbe voluto dire due
 * drawer che divergono al primo campo nuovo; il calendario apre esattamente
 * questo, ed è quello che rende le due viste la stessa cosa vista da due lati.
 */

import { Check, HelpCircle, Link2, Send, Sparkles, Truck, X } from "lucide-react";
import { useEffect, useState } from "react";

import type { AvvioChat } from "@/components/m3/AidaSidebar";
import { eCongelato, NDP_LABEL, StatoBadge } from "@/components/m3/stato-carico";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCaricoPrevisto,
  useCpAggancia,
  useCpPartenza,
  useCpPatch,
  useCpSollecito,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { CaricoPrevistoDetail, CaricoPrevistoPatch } from "@/types/api";


export function CaricoPrevistoDrawer({
  id,
  onClose,
  onApriChat,
}: {
  id: string;
  onClose: () => void;
  onApriChat?: (avvio: AvvioChat) => void;
}) {
  const { data: det, isLoading } = useCaricoPrevisto(id);
  return (
    <div className="fixed inset-0 top-14 z-40 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-xl flex-col overflow-y-auto border-l bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{det?.cliente_nome ?? "Carico previsto"}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
        {isLoading || !det ? (
          <div className="p-4"><Skeleton className="h-80 w-full" /></div>
        ) : (
          <DrawerCorpo det={det} onApriChat={onApriChat} />
        )}
      </div>
    </div>
  );
}

function DrawerCorpo({
  det,
  onApriChat,
}: {
  det: CaricoPrevistoDetail;
  onApriChat?: (avvio: AvvioChat) => void;
}) {
  const patch = useCpPatch(det.id);
  const partenza = useCpPartenza(det.id);
  const aggancia = useCpAggancia(det.id);
  const sollecito = useCpSollecito(det.id);
  const [note, setNote] = useState(det.note ?? "");
  const [ordini, setOrdini] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  const chiuso = eCongelato(det.stato);
  const attesi = new Set((det.domanda?.campi ?? []).map((c) => c.chiave));

  return (
    <div className="flex-1 space-y-5 p-4 text-sm">
      {/* La domanda aperta, prima di tutto il resto: è l'unica cosa che
          richiede un'azione di Marina e non di Aida. */}
      {det.domanda && (
        <BoxDomanda
          det={det}
          onFatto={(chiusa) =>
            setMsg(chiusa ? "Grazie: domanda chiusa." : "Salvato — manca ancora qualcosa.")
          }
          onApriChat={onApriChat}
        />
      )}

      {/* Fatti rapidi */}
      <div className="grid grid-cols-2 gap-2">
        <Fatto label="Stato"><StatoBadge stato={det.stato} /></Fatto>
        <Fatto label="Tempo cliente">
          {det.genesi === "ndp" ? "n/d" : det.lead_time_giorni != null ? `${det.lead_time_giorni} gg` : "—"}
        </Fatto>
        <Fatto label="Avvisato il">
          {det.avvisato_il ? new Date(det.avvisato_il).toLocaleDateString("it-IT") : "—"}
        </Fatto>
        <Fatto label="Data ritiro">
          {det.data_ritiro ? new Date(det.data_ritiro).toLocaleDateString("it-IT") : "—"}
          {det.data_ritiro_tipo && <span className="ml-1 text-xs text-muted-foreground">({det.data_ritiro_tipo})</span>}
        </Fatto>
        <Fatto label="NDP">{NDP_LABEL[det.ndp_badge] ?? det.ndp_badge}{det.ndp_numero ? ` · ${det.ndp_numero}` : ""}</Fatto>
        <Fatto label="Rif. commessa">{det.commesse.join(", ") || "—"}</Fatto>
      </div>

      {/* Riferimenti cliente → commessa */}
      <Sezione titolo="Riferimenti cliente → commessa">
        {det.riferimenti_map.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nessun riferimento citato.</p>
        ) : (
          <ul className="space-y-1.5">
            {det.riferimenti_map.map((m) => (
              <li key={m.riferimento} className="flex items-center gap-2">
                <code className="rounded bg-muted px-1">{m.riferimento}</code>
                {m.ordine_numero != null ? (
                  <span className="text-emerald-600">→ commessa {m.ordine_numero}</span>
                ) : (
                  <span className="flex items-center gap-1">
                    <input
                      className="h-7 w-24 rounded border bg-card px-1.5 text-xs"
                      placeholder="n. commessa"
                      value={ordini[m.riferimento] ?? ""}
                      onChange={(e) => setOrdini((o) => ({ ...o, [m.riferimento]: e.target.value }))}
                    />
                    <button
                      type="button"
                      disabled={aggancia.isPending || !/^\d+$/.test(ordini[m.riferimento] ?? "")}
                      onClick={() =>
                        aggancia.mutate({
                          riferimento: m.riferimento,
                          ordine_numero: Number(ordini[m.riferimento]),
                        })
                      }
                      className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                    >
                      <Link2 className="h-3 w-3" /> Aggancia
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Sezione>

      {/* Dati del tabellone: i 4 campi che Aida può chiedere sono anche i soli
          che Marina può compilare. Stato e NDP restano derivati dai documenti. */}
      <Sezione titolo="Dati del tabellone">
        <div className="space-y-2">
          <CampoTabellone
            label="Data ritiro"
            tipo="date"
            valore={det.data_ritiro}
            fonte={det.campi_fonte?.data_ritiro}
            atteso={attesi.has("data_ritiro")}
            onSalva={(v) => patch.mutate({ data_ritiro: v }, { onSuccess: () => setMsg("Salvato.") })}
          />
          <CampoTabellone
            label="Pronto dal"
            tipo="date"
            valore={det.pronto_dal}
            fonte={det.campi_fonte?.pronto_dal}
            atteso={attesi.has("pronto_dal")}
            onSalva={(v) => patch.mutate({ pronto_dal: v }, { onSuccess: () => setMsg("Salvato.") })}
          />
          <CampoTabellone
            label="Pallet"
            tipo="number"
            valore={det.pallet_previsti != null ? String(det.pallet_previsti) : null}
            fonte={det.campi_fonte?.pallet}
            atteso={attesi.has("pallet")}
            onSalva={(v) =>
              patch.mutate(
                { pallet: v === null ? null : Number(v) },
                { onSuccess: () => setMsg("Salvato.") },
              )
            }
          />
          <CampoTabellone
            label="Commessa"
            tipo="testo-derivato"
            valore={det.commesse.join(", ") || null}
            fonte={det.campi_fonte?.commessa}
            nota="si aggancia dai riferimenti, qui sopra"
          />
        </div>
      </Sezione>

      {/* Partenza. Qui si PIANIFICA («parte giovedì»); il fatto — «è uscito» — si
          registra dalla riga, con un click, perché è lì che sta l'occhio. */}
      <Sezione titolo="Partenza prevista">
        {!det.puo_partire && !det.data_partenza_prevista ? (
          <p className="text-xs text-muted-foreground">
            Serve la NDP (o il DDT) prima di poter pianificare la partenza.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              className="h-8 rounded-md border-2 border-border bg-card px-2 text-xs shadow-sm"
              value={det.data_partenza_prevista ?? ""}
              onChange={(e) =>
                partenza.mutate(
                  { giorno: e.target.value || null },
                  {
                    onSuccess: () =>
                      setMsg(e.target.value ? "Giorno di partenza salvato." : "Pianificazione tolta."),
                  },
                )
              }
            />
            {!det.partito_il && (
              <button
                type="button"
                disabled={partenza.isPending}
                onClick={() =>
                  partenza.mutate({ uscito: true }, { onSuccess: () => setMsg("Uscita registrata.") })
                }
                className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                <Truck className="h-3.5 w-3.5" /> Conferma uscita
              </button>
            )}
            {det.partito_il && (
              <span className="text-xs font-medium text-emerald-700">
                Uscito il {new Date(det.partito_il).toLocaleDateString("it-IT")}
              </span>
            )}
            <span className="basis-full text-[11px] text-muted-foreground">
              Quando attiveremo i DDT l'uscita si registrerà da sé: il documento che
              accompagna la merce è già la prova che il camion è partito.
            </span>
          </div>
        )}
      </Sezione>

      {/* Carico (composizione) */}
      <Sezione titolo="Carico">
        <span className={cn("mb-1.5 inline-block rounded px-1.5 py-0.5 text-[11px]",
          det.composizione_fonte === "ndp" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>
          {det.composizione_fonte === "ndp" ? "da NDP" : "ipotizzato · pre-NDP"}
        </span>
        {det.composizione.length === 0 ? (
          <p className="text-xs text-muted-foreground">Composizione non disponibile.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr><th className="text-left">Articolo</th><th className="text-right">Colli</th><th className="text-right">Pallet</th><th className="text-left pl-2">Lotti</th></tr>
            </thead>
            <tbody>
              {det.composizione.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="py-1">{r.descrizione || r.codice}</td>
                  <td className="py-1 text-right tabular-nums">{r.colli ?? "—"}</td>
                  <td className="py-1 text-right tabular-nums">{r.pallet ?? "—"}</td>
                  <td className="py-1 pl-2">{r.lotti.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Sezione>

      {/* Conversazione */}
      <Sezione titolo="Conversazione">
        {det.thread.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nessun messaggio.</p>
        ) : (
          <ul className="space-y-2">
            {det.thread.map((v, i) => (
              <li key={i} className={cn("rounded-md border p-2 text-xs", v.direzione === "in" ? "bg-blue-50" : "bg-muted/40")}>
                <div className="mb-0.5 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{v.direzione === "in" ? "← " : "→ "}{v.mittente}</span>
                  {v.classificazione && <span className="italic">{v.classificazione}</span>}
                </div>
                <div className="whitespace-pre-wrap">{v.corpo}</div>
              </li>
            ))}
          </ul>
        )}
      </Sezione>

      {/* Nota Aida da verificare */}
      {det.da_verificare && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Lettura Aida <b>da verificare</b>: controlla i dati e conferma con «Conferma lettura».</span>
        </div>
      )}

      {/* Note */}
      <Sezione titolo="Note">
        <textarea
          className="h-20 w-full rounded-md border bg-card p-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note libere di Marina…"
        />
      </Sezione>

      {/* Footer azioni */}
      <div className="flex flex-wrap gap-2 border-t pt-3">
        <button
          type="button"
          disabled={patch.isPending}
          onClick={() => patch.mutate({ note }, { onSuccess: () => setMsg("Nota salvata.") })}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          Salva nota
        </button>
        {det.da_verificare && (
          <button
            type="button"
            disabled={patch.isPending}
            onClick={() => patch.mutate({ conferma_lettura: true }, { onSuccess: () => setMsg("Lettura confermata.") })}
            className="rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
          >
            Conferma lettura
          </button>
        )}
        {!chiuso && (
          <button
            type="button"
            disabled={sollecito.isPending}
            onClick={() => sollecito.mutate(undefined, { onSuccess: () => setMsg("Sollecito creato in Bacheca per Marina.") })}
            className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" /> Sollecito a Marina
          </button>
        )}
        {msg && <span className="self-center text-xs text-emerald-600">{msg}</span>}
      </div>
    </div>
  );
}

function Fatto({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Sezione({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-foreground">{titolo}</div>
      {children}
    </div>
  );
}


/**
 * «Aida ha chiesto»: la domanda aperta, con un campo per ciascun dato.
 *
 * Rispondere non deve voler dire scrivere una frase. Marina ha già dimostrato
 * di non rispondere «1/2/3» alle email; se le chiediamo di conversare per dire
 * «18» faremo la stessa fine. Compilare il campo È la risposta — e passa dallo
 * stesso servizio della risposta via email, quindi nel pannello admin risulta
 * «risposta dal portale».
 */
function BoxDomanda({
  det,
  onFatto,
  onApriChat,
}: {
  det: CaricoPrevistoDetail;
  onFatto: (chiusa: boolean) => void;
  onApriChat?: (avvio: AvvioChat) => void;
}) {
  const patch = useCpPatch(det.id);
  const domanda = det.domanda;
  if (!domanda) return null;

  const CHIAVI: Record<string, { tipo: "date" | "number"; campo: keyof CaricoPrevistoPatch }> = {
    data_ritiro: { tipo: "date", campo: "data_ritiro" },
    pronto_dal: { tipo: "date", campo: "pronto_dal" },
    pallet: { tipo: "number", campo: "pallet" },
  };
  // La commessa non si compila a mano: si aggancia dai riferimenti, che è la
  // strada che fa imparare la mappa. E non tutte le domande chiedono campi del
  // tabellone — quelle sui dati del plico hanno un percorso loro: proporre
  // l'aggancio commessa lì sarebbe un suggerimento sbagliato.
  const chiedibili = domanda.campi.filter((c) => c.chiave in CHIAVI);
  const suCommessa = domanda.campi.some((c) => c.chiave === "commessa");

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="mb-2 flex items-start gap-2">
        <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
            Aida {domanda.parziale ? "aspetta ancora" : "ha chiesto"}
            <span className="ml-1.5 font-normal normal-case tracking-normal text-amber-700">
              · {domanda.giorni_aperta} gg
            </span>
          </p>
          <p className="mt-0.5 text-sm text-amber-900">{domanda.testo}</p>
          {domanda.parziale && (
            <p className="mt-0.5 text-xs italic text-amber-700">
              Una parte è arrivata. Resta {domanda.campi.map((c) => c.label).join(", ")}.
            </p>
          )}
        </div>
      </div>

      {chiedibili.length === 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-amber-800">
            {suCommessa
              ? "Aggancia il riferimento alla commessa qui sotto: la domanda si chiude da sé."
              : "Questa domanda non riguarda i campi del tabellone."}
          </p>
          {!suCommessa && onApriChat && (
            <button
              type="button"
              onClick={() =>
                onApriChat({
                  chiave: `${domanda.id}-${Date.now()}`,
                  richiestaId: domanda.id,
                  domanda: `Sulla riga di ${det.cliente_nome}${
                    det.commesse.length ? ` (commessa ${det.commesse.join(", ")})` : ""
                  } ti ho chiesto: ${domanda.testo}. Dimmi pure.`,
                })
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              <Sparkles className="h-3.5 w-3.5" /> Rispondi nella chat con Aida
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          {chiedibili.map((c) => {
            const { tipo, campo } = CHIAVI[c.chiave];
            return (
              <label key={c.chiave} className="flex items-center gap-2">
                <span className="w-28 shrink-0 text-xs font-medium text-amber-900">{c.label}</span>
                <input
                  type={tipo}
                  step={tipo === "number" ? "1" : undefined}
                  min={tipo === "number" ? "0" : undefined}
                  disabled={patch.isPending}
                  className="h-8 flex-1 rounded border border-amber-300 bg-white px-2 text-xs disabled:opacity-50"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    patch.mutate(
                      { [campo]: tipo === "number" ? Number(v) : v } as CaricoPrevistoPatch,
                      // `chiusa` = era l'ultimo campo che mancava. Se no, il box
                      // resta con quello che manca ancora.
                      { onSuccess: (agg) => onFatto(agg.domanda == null) },
                    );
                  }}
                />
              </label>
            );
          })}
          <p className="pt-0.5 text-[11px] text-amber-700">
            Compilarne uno chiude la domanda. Se serve spiegare qualcosa, usa la chat con Aida.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Campo del tabellone con la sua provenienza accanto. La provenienza è discreta
 * ma sempre presente: è ciò che rende il tabellone affidabile agli occhi di
 * Marina — e dice a noi se Aida sta leggendo bene.
 */
function CampoTabellone({
  label,
  tipo,
  valore,
  fonte,
  nota,
  atteso,
  onSalva,
}: {
  label: string;
  tipo: "date" | "number" | "testo-derivato";
  valore: string | null;
  fonte?: { fonte: string; il: string; via?: string };
  nota?: string;
  atteso?: boolean;
  onSalva?: (v: string | null) => void;
}) {
  const [bozza, setBozza] = useState(valore ?? "");
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setBozza(valore ?? ""), [valore]);
  useEffect(() => {
    if (!salvato) return;
    const t = setTimeout(() => setSalvato(false), 1800);
    return () => clearTimeout(t);
  }, [salvato]);

  function salva(v: string | null) {
    if ((v ?? "") === (valore ?? "")) return;
    onSalva?.(v);
    setSalvato(true);
  }

  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs text-muted-foreground">{label}</span>
      {tipo === "testo-derivato" ? (
        <span className="flex-1 text-sm">
          {valore ?? <span className="text-muted-foreground">—</span>}
          {nota && <span className="ml-1 text-[11px] text-muted-foreground">({nota})</span>}
        </span>
      ) : (
        <span className="flex flex-1 items-center gap-1">
          <input
            type={tipo}
            step={tipo === "number" ? "1" : undefined}
            min={tipo === "number" ? "0" : undefined}
            value={bozza}
            onChange={(e) => setBozza(e.target.value)}
            onBlur={() => salva(bozza || null)}
            onKeyDown={(e) => {
              if (e.key === "Enter") salva(bozza || null);
            }}
            // Bordo pieno + sfondo chiaro: alla prova non si capiva che fossero
            // campi da compilare. Ambra quando è il dato che Aida aspetta.
            className={cn(
              "h-8 flex-1 rounded-md border-2 bg-card px-2 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-ring",
              atteso && !valore ? "border-amber-300 bg-amber-50" : "border-border",
            )}
          />
          {valore && (
            <button
              type="button"
              onClick={() => {
                setBozza("");
                salva(null);
              }}
              title="Svuota il campo"
              className="rounded p-0.5 text-muted-foreground hover:text-red-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
        </span>
      )}
      <Provenienza fonte={fonte} />
    </div>
  );
}

function Provenienza({ fonte }: { fonte?: { fonte: string; il: string; via?: string } }) {
  if (!fonte) return <span className="w-20 shrink-0" />;
  const ETICHETTA: Record<string, string> = {
    aida: "letto da Aida",
    marina: "detto da te",
    ndp: "dalla NDP",
    m1: "da sistema",
  };
  const quando = new Date(fonte.il).toLocaleDateString("it-IT");
  return (
    <span
      title={`${ETICHETTA[fonte.fonte] ?? fonte.fonte} il ${quando}${fonte.via ? ` (${fonte.via})` : ""}`}
      className={cn(
        "w-20 shrink-0 truncate text-[11px]",
        fonte.fonte === "marina" ? "text-emerald-700" : "text-muted-foreground",
      )}
    >
      {ETICHETTA[fonte.fonte] ?? fonte.fonte}
    </span>
  );
}
